import React, { ErrorInfo, ReactNode, createContext } from 'react';
import { logger } from 'src/utils';
import { Logs } from 'src/constants/metrics';

/**
 * This component serves as a catch all for now. We may need to look at
 * refactoring this component to include string translations
 */
type ErrorBoundaryProps = {
  children: ReactNode;
};
type ErrorBoundaryState = {
  hasError: boolean
};

const ErrorBoundaryContext = createContext({});
const domManipulationErrorMessage = "Failed to execute 'removeChild' on 'Node'";

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const logContext = {
      errorInfo,
      href: window.location.href,
    };
    if (error.message.includes(domManipulationErrorMessage)) {
      logger.error(Logs.domManipulationException, error, logContext);
    } else {
      logger.error(Logs.componentUnhandledException, error, logContext);
    }
  }

  handleError = () => {
    this.setState({ hasError: true });
  };

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return (
        <div className="container m-6">
          <h1>Encountered an unexpected error</h1>
          <div>Please refresh the page and try again</div>
        </div>
      );
    }

    return (
      <ErrorBoundaryContext.Provider value={this.handleError}>
        {children}
      </ErrorBoundaryContext.Provider>
    );
  }
}

export const withError = <P extends object>(Component: React.ComponentType<P>) => (props: any) => (
  <ErrorBoundaryContext.Consumer>
    {(handleError) => <Component {...props} handleError={handleError} />}
  </ErrorBoundaryContext.Consumer>
);

export default ErrorBoundary;
